import React, { useState, useEffect } from 'react';
import { perizinanIptService } from '../../../services/perizinanIptService';
import { useAuth } from '../../Admin/context/AuthContext';
import {
    FaSave, FaPlus, FaEdit, FaTrash, FaFilePdf, FaTimes,
    FaCheckCircle, FaExclamationTriangle, FaDownload,
    FaMoneyBillWave, FaClock, FaList
} from 'react-icons/fa';

const ManajemenPerizinanIpt = () => {
    const { user } = useAuth();
    const hasPermission = (p) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        return user?.permissions?.includes(p) || false;
    };

    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);

    // Setting
    const [setting, setSetting] = useState({
        biaya_retribusi_tarif: '',
        biaya_retribusi_keterangan: '',
        biaya_administrasi_tarif: '',
        biaya_administrasi_keterangan: '',
        biaya_survey_tarif: '',
        biaya_survey_keterangan: '',
        waktu_verifikasi: '',
        waktu_survey: '',
        waktu_penerbitan: '',
        waktu_total_catatan: '',
        header_judul: '',
        header_deskripsi: '',
    });

    // Files
    const [files, setFiles] = useState([]);
    const [showFileForm, setShowFileForm] = useState(false);
    const [editingFile, setEditingFile] = useState(null);
    const [fileFormData, setFileFormData] = useState({ judul: '', file: null });
    const [deleteFileConfirm, setDeleteFileConfirm] = useState(null);

    // FAQ
    const [faqs, setFaqs] = useState([]);
    const [showFaqForm, setShowFaqForm] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [faqFormData, setFaqFormData] = useState({ pertanyaan: '', jawaban: '', order: 0 });
    const [deleteFaqConfirm, setDeleteFaqConfirm] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('biaya');

    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const loadAll = async () => {
        setLoading(true);
        const [settingRes, filesRes, faqsRes] = await Promise.all([
            perizinanIptService.getSetting(),
            perizinanIptService.getFiles(),
            perizinanIptService.getFaqs(),
        ]);
        if (settingRes.success && settingRes.data) {
            setSetting(prev => ({ ...prev, ...settingRes.data }));
        }
        if (filesRes.success) setFiles(filesRes.data);
        if (faqsRes.success) setFaqs(faqsRes.data);
        setLoading(false);
    };

    useEffect(() => {
        loadAll();
    }, []);

    // ============ SETTING ============
    const handleSettingChange = (e) => {
        const { name, value } = e.target;
        setSetting(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveSetting = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const result = await perizinanIptService.updateSetting(setting);
        if (result.success) {
            showNotif(result.message || 'Setting berhasil disimpan');
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    // ============ FILES ============
    const openCreateFile = () => {
        setEditingFile(null);
        setFileFormData({ judul: '', file: null });
        setShowFileForm(true);
    };

    const openEditFile = (f) => {
        setEditingFile(f);
        setFileFormData({ judul: f.judul, file: null });
        setShowFileForm(true);
    };

    const handleSubmitFile = async (e) => {
        e.preventDefault();
        if (!editingFile && !fileFormData.file) {
            showNotif('File wajib dipilih', 'error');
            return;
        }
        setSubmitting(true);

        const fd = new FormData();
        fd.append('judul', fileFormData.judul);
        if (fileFormData.file) fd.append('file', fileFormData.file);

        let result;
        if (editingFile) {
            result = await perizinanIptService.updateFile(editingFile.id, fd);
        } else {
            result = await perizinanIptService.createFile(fd);
        }

        if (result.success) {
            showNotif(result.message || 'File berhasil disimpan');
            setShowFileForm(false);
            loadAll();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDeleteFile = async () => {
        if (!deleteFileConfirm) return;
        const result = await perizinanIptService.deleteFile(deleteFileConfirm.id);
        if (result.success) {
            showNotif('File berhasil dihapus');
            loadAll();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteFileConfirm(null);
    };

    // ============ FAQ ============
    const openCreateFaq = () => {
        setEditingFaq(null);
        setFaqFormData({ pertanyaan: '', jawaban: '', order: faqs.length + 1 });
        setShowFaqForm(true);
    };

    const openEditFaq = (f) => {
        setEditingFaq(f);
        setFaqFormData({ pertanyaan: f.pertanyaan, jawaban: f.jawaban, order: f.order || 0 });
        setShowFaqForm(true);
    };

    const handleSubmitFaq = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        let result;
        if (editingFaq) {
            result = await perizinanIptService.updateFaq(editingFaq.id, faqFormData);
        } else {
            result = await perizinanIptService.createFaq(faqFormData);
        }
        if (result.success) {
            showNotif('FAQ berhasil disimpan');
            setShowFaqForm(false);
            loadAll();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDeleteFaq = async () => {
        if (!deleteFaqConfirm) return;
        const result = await perizinanIptService.deleteFaq(deleteFaqConfirm.id);
        if (result.success) {
            showNotif('FAQ berhasil dihapus');
            loadAll();
        }
        setDeleteFaqConfirm(null);
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Perizinan IPT</h2>
                <p className="text-gray-600">Kelola biaya, waktu, formulir, dan FAQ layanan IPT</p>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6 border-b">
                {[
                    { id: 'biaya', label: 'Biaya & Waktu', icon: FaMoneyBillWave },
                    { id: 'formulir', label: 'Formulir Download', icon: FaDownload },
                    { id: 'faq', label: 'FAQ', icon: FaList },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                            ? 'border-[#0D9488] text-[#0D9488]'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <tab.icon className="mr-2" /> {tab.label}
                    </button>
                ))}
            </div>

            {/* ============ TAB: BIAYA & WAKTU ============ */}
            {activeTab === 'biaya' && hasPermission('perizinan-ipt.update') && (
                <form onSubmit={handleSaveSetting} className="space-y-6">
                    {/* Biaya */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-bold text-[#0F4C3A] mb-4 flex items-center">
                            <FaMoneyBillWave className="mr-2 text-[#0D9488]" /> Biaya Layanan
                        </h3>

                        <div className="space-y-4">
                            {[
                                { key: 'retribusi', label: 'Retribusi IPT' },
                                { key: 'administrasi', label: 'Biaya Administrasi' },
                                { key: 'survey', label: 'Biaya Survey' },
                            ].map(item => (
                                <div key={item.key} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Jenis</label>
                                        <input type="text" value={item.label} readOnly className="w-full px-3 py-2 border rounded-lg bg-gray-100" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tarif</label>
                                        <input
                                            type="text"
                                            name={`biaya_${item.key}_tarif`}
                                            value={setting[`biaya_${item.key}_tarif`] || ''}
                                            onChange={handleSettingChange}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                            placeholder="Contoh: Rp 50.000"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Keterangan</label>
                                        <input
                                            type="text"
                                            name={`biaya_${item.key}_keterangan`}
                                            value={setting[`biaya_${item.key}_keterangan`] || ''}
                                            onChange={handleSettingChange}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Waktu */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-bold text-[#0F4C3A] mb-4 flex items-center">
                            <FaClock className="mr-2 text-[#0D9488]" /> Estimasi Waktu
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Verifikasi Dokumen</label>
                                <input
                                    type="text"
                                    name="waktu_verifikasi"
                                    value={setting.waktu_verifikasi || ''}
                                    onChange={handleSettingChange}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    placeholder="Contoh: 2-3 Hari"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Survey & Penilaian</label>
                                <input
                                    type="text"
                                    name="waktu_survey"
                                    value={setting.waktu_survey || ''}
                                    onChange={handleSettingChange}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    placeholder="Contoh: 5-7 Hari"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Penerbitan Izin</label>
                                <input
                                    type="text"
                                    name="waktu_penerbitan"
                                    value={setting.waktu_penerbitan || ''}
                                    onChange={handleSettingChange}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    placeholder="Contoh: 7 Hari"
                                />
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium mb-1">Catatan Total Waktu</label>
                            <textarea
                                name="waktu_total_catatan"
                                value={setting.waktu_total_catatan || ''}
                                onChange={handleSettingChange}
                                rows="2"
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                placeholder="Contoh: Total waktu proses: 14-21 hari kerja"
                            />
                        </div>
                    </div>

                    {/* Header */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-bold text-[#0F4C3A] mb-4">Header Halaman</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium mb-1">Judul</label>
                                <input
                                    type="text"
                                    name="header_judul"
                                    value={setting.header_judul || ''}
                                    onChange={handleSettingChange}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Deskripsi</label>
                                <textarea
                                    name="header_deskripsi"
                                    value={setting.header_deskripsi || ''}
                                    onChange={handleSettingChange}
                                    rows="2"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                        >
                            {submitting ? 'Menyimpan...' : <><FaSave className="mr-2" /> Simpan Semua</>}
                        </button>
                    </div>
                </form>
            )}

            {/* ============ TAB: FORMULIR ============ */}
            {activeTab === 'formulir' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-[#0F4C3A]">Daftar Formulir</h3>
                        {hasPermission('perizinan-ipt.create') && (
                            <button onClick={openCreateFile} className="btn-primary flex items-center">
                                <FaPlus className="mr-2" /> Tambah File
                            </button>
                        )}
                    </div>

                    {files.length === 0 ? (
                        <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                            <FaFilePdf className="text-6xl text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">Belum ada file formulir</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {files.map(f => (
                                <div key={f.id} className="bg-white rounded-xl shadow-sm p-4 flex items-start gap-3 hover:shadow-md transition-shadow">
                                    <FaFilePdf className="text-red-500 text-3xl flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-800 truncate">{f.judul}</p>
                                        <p className="text-xs text-gray-500 truncate">{f.file_name}</p>
                                        <p className="text-xs text-gray-400">{formatFileSize(f.file_size)}</p>
                                        <div className="flex gap-1 mt-2">
                                            {hasPermission('perizinan-ipt.update') && (
                                                <button onClick={() => openEditFile(f)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Edit">
                                                    <FaEdit size={12} />
                                                </button>
                                            )}
                                            {hasPermission('perizinan-ipt.delete') && (
                                                <button onClick={() => setDeleteFileConfirm(f)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Hapus">
                                                    <FaTrash size={12} />
                                                </button>
                                            )}
                                            <a href={getFileUrl(f.file_path)} target="_blank" rel="noopener noreferrer" className="p-1.5 text-purple-500 hover:bg-purple-50 rounded" title="Download">
                                                <FaDownload size={12} />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ============ TAB: FAQ ============ */}
            {activeTab === 'faq' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-[#0F4C3A]">Daftar FAQ</h3>
                        {hasPermission('perizinan-ipt.create') && (
                            <button onClick={openCreateFaq} className="btn-primary flex items-center">
                                <FaPlus className="mr-2" /> Tambah FAQ
                            </button>
                        )}
                    </div>

                    {faqs.length === 0 ? (
                        <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                            <p className="text-gray-500">Belum ada FAQ</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {faqs.map(f => (
                                <div key={f.id} className="bg-white rounded-xl shadow-sm p-4">
                                    <div className="flex justify-between items-start gap-3">
                                        <div className="flex-1">
                                            <p className="font-semibold text-[#0F4C3A]">Q: {f.pertanyaan}</p>
                                            <p className="text-sm text-gray-600 mt-1">A: {f.jawaban}</p>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0">
                                            {hasPermission('perizinan-ipt.update') && (
                                                <button onClick={() => openEditFaq(f)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Edit">
                                                    <FaEdit size={14} />
                                                </button>
                                            )}
                                            {hasPermission('perizinan-ipt.delete') && (
                                                <button onClick={() => setDeleteFaqConfirm(f)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Hapus">
                                                    <FaTrash size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ============ MODAL FILE ============ */}
            {showFileForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-[#0F4C3A]">
                                {editingFile ? 'Edit File' : 'Tambah File'}
                            </h3>
                            <button onClick={() => setShowFileForm(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleSubmitFile} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Judul *</label>
                                <input
                                    type="text"
                                    value={fileFormData.judul}
                                    onChange={(e) => setFileFormData({ ...fileFormData, judul: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    File {!editingFile && '*'}
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                    onChange={(e) => setFileFormData({ ...fileFormData, file: e.target.files[0] })}
                                    className="w-full px-4 py-2 border rounded-lg"
                                />
                                <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, XLS, XLSX • Maks 10MB</p>
                                {editingFile && !fileFormData.file && (
                                    <p className="text-xs text-gray-500 mt-1">File saat ini: {editingFile.file_name}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={() => setShowFileForm(false)} className="px-6 py-2 border rounded-lg">Batal</button>
                                <button type="submit" disabled={submitting} className="bg-[#2E7D32] text-white px-6 py-2 rounded-lg disabled:opacity-50">
                                    {submitting ? 'Menyimpan...' : 'Simpan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ============ MODAL FAQ ============ */}
            {showFaqForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-[#0F4C3A]">
                                {editingFaq ? 'Edit FAQ' : 'Tambah FAQ'}
                            </h3>
                            <button onClick={() => setShowFaqForm(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleSubmitFaq} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Pertanyaan *</label>
                                <textarea
                                    value={faqFormData.pertanyaan}
                                    onChange={(e) => setFaqFormData({ ...faqFormData, pertanyaan: e.target.value })}
                                    rows="2"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Jawaban *</label>
                                <textarea
                                    value={faqFormData.jawaban}
                                    onChange={(e) => setFaqFormData({ ...faqFormData, jawaban: e.target.value })}
                                    rows="3"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    required
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={() => setShowFaqForm(false)} className="px-6 py-2 border rounded-lg">Batal</button>
                                <button type="submit" disabled={submitting} className="bg-[#2E7D32] text-white px-6 py-2 rounded-lg disabled:opacity-50">
                                    {submitting ? 'Menyimpan...' : 'Simpan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ============ DELETE FILE CONFIRM ============ */}
            {deleteFileConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <FaTrash className="text-4xl text-red-500 mx-auto mb-3" />
                        <p className="mb-6">Hapus file <strong>{deleteFileConfirm.judul}</strong>?</p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteFileConfirm(null)} className="px-6 py-2.5 border rounded-lg">Batal</button>
                            <button onClick={handleDeleteFile} className="px-6 py-2.5 bg-red-500 text-white rounded-lg">Hapus</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ DELETE FAQ CONFIRM ============ */}
            {deleteFaqConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <FaTrash className="text-4xl text-red-500 mx-auto mb-3" />
                        <p className="mb-6">Hapus FAQ ini?</p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteFaqConfirm(null)} className="px-6 py-2.5 border rounded-lg">Batal</button>
                            <button onClick={handleDeleteFaq} className="px-6 py-2.5 bg-red-500 text-white rounded-lg">Hapus</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenPerizinanIpt;